/**
 * @file experienceDetailsHero.component.tsx
 * @path apps/website/src/features/experiences/components/experienceDetailsHero.component.tsx
 * @description UI component with conditional link support for the booking action.
 */

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
  Button,
} from "@sirbastians/ui";
import React from "react";
import { ArrowRight, Tag } from "react-bootstrap-icons";
import { Link } from "react-router-dom";

import { PATHS } from "@/config";
import { ExperienceDetail } from "@/features";

export const ExperienceDetailsHero = ({ data }: { data: ExperienceDetail }) => {
  // ✨ Check if the booking link is an external URL
  const isExternalBooking =
    data.bookingLink?.startsWith("http") || data.bookingLink?.startsWith("//");

  return (
    <section className="grid grid-cols-1 lg:grid-cols-3 lg:gap-20 items-center mb-20">
      <div className="flex flex-col col-span-2 justify-center space-y-8 mb-20 lg:mb-0 lg:pr-8">
        <div>
          <Breadcrumb className="mb-6">
            <BreadcrumbList className="text-[10px] font-montserrat font-black uppercase tracking-[0.3em]">
              {data.breadcrumbPaths.map((path: string, index: number) => {
                const isLast = index === data.breadcrumbPaths.length - 1;
                return (
                  <React.Fragment key={path}>
                    <BreadcrumbItem>
                      <BreadcrumbLink
                        asChild
                        className="text-muted-foreground hover:text-accent transition-colors"
                      >
                        <Link to={index === 0 ? PATHS.ROOT : PATHS.EXPERIENCES}>
                          {path}
                        </Link>
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    {!isLast && <BreadcrumbSeparator className="text-muted" />}
                  </React.Fragment>
                );
              })}
            </BreadcrumbList>
          </Breadcrumb>

          <h1 className="text-5xl md:text-6xl lg:text-[4.5rem] font-montserrat font-bold tracking-tighter text-foreground leading-[1.05] mb-8">
            {data.title}
          </h1>

          {data.subtitle && (
            <p className="text-xl font-open-sans text-muted-foreground font-light leading-relaxed max-w-xl">
              {data.subtitle}
            </p>
          )}
        </div>

        {/* Tags */}
        {data.tags && data.tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {data.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-montserrat font-bold uppercase tracking-widest bg-secondary text-muted-foreground border border-border"
              >
                <Tag className="h-3 w-3" />
                {tag}
              </span>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-4">
          <Button asChild variant="accent" size="lg">
            {/* ✨ CONDITIONAL LINK RENDERING */}
            {isExternalBooking ? (
              <a
                href={data.bookingLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4"
              >
                Book Now
                <ArrowRight className="h-4 w-4" />
              </a>
            ) : (
              <Link to={data.bookingLink} className="flex items-center gap-4">
                Book Now
                <ArrowRight className="h-4 w-4" />
              </Link>
            )}
          </Button>

          <Button asChild variant="secondary" size="lg">
            <Link to={PATHS.CONTACT}>Enquire</Link>
          </Button>
        </div>
      </div>

      <div className="w-full aspect-square overflow-hidden rounded-[2.5rem] bg-secondary neuro-bezel">
        <img
          src={data.mainImage.src}
          alt={data.mainImage.alt}
          className="w-full h-full object-cover transition-transform duration-1000 hover:scale-105"
        />
      </div>
    </section>
  );
};
